// Header.tsx

import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import {
  Link,
  useLocation,
} from "react-router-dom";

import LoginAndSignupPopup from "./LoginAndSignup";

import GrowniverseLogo from "../Photos/Logo/growniverse-logo-full-bordered.svg";

const Header: React.FC = () => {
  const [isModalOpen, setIsModalOpen] =
    useState(false);

  const [mobileMenuOpen, setMobileMenuOpen] =
    useState(false);

  const location = useLocation();

  // ============================================================
  // LOGIN MODAL
  // ============================================================

  const openModal = () => {
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
  };

  // ============================================================
  // PAGE
  // ============================================================

  const isHomePage =
    location.pathname === "/";

  const isActive = (path: string) =>
    location.pathname === path;

  // ============================================================
  // AFFILIATE LINK
  // ============================================================

  const query = useMemo(
    () =>
      new URLSearchParams(
        location.search
      ),
    [location.search]
  );

  const runOnce = useRef(false);

  useEffect(() => {
    if (runOnce.current === false) {
      const sponserGID =
        query.get("affiliatelink");

      if (sponserGID) {
        setIsModalOpen(true);
      }

      runOnce.current = true;
    }
  }, [query]);

  // ============================================================
  // CLOSE MOBILE MENU WHEN PAGE CHANGES
  // ============================================================

  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  // ============================================================
  // NAVIGATION
  // ============================================================

  const navigationItems = [
    {
      label: "Home",
      path: "/",
    },
    {
      label: "About Us",
      path: "/about",
    },
    {
      label: "Contact Us",
      path: "/contact",
    },
    {
      label: "Courses",
      path: "/courses",
    },
    {
      label: "Certificate",
      path: "/certificate-verification",
    },
  ];

  return (
    <>
      {/* ========================================================
          FUTURISTIC HEADER
      ======================================================== */}

      <header
        style={{
          position: isHomePage
            ? "absolute"
            : "relative",

          top: 0,
          left: 0,

          zIndex: 1200,

          width: "100%",

          minHeight: 82,

          display: "flex",
          alignItems: "center",

          background:
            "linear-gradient(135deg, rgba(5,8,16,0.97), rgba(8,12,24,0.95))",

          borderBottom:
            "1px solid rgba(0,246,255,0.16)",

          boxShadow:
            "0 8px 35px rgba(0,0,0,0.45), inset 0 -1px 0 rgba(255,255,255,0.025)",

          overflow: "hidden",
        }}
      >
        {/* ======================================================
            BACKGROUND GRID
        ====================================================== */}

        <div
          style={{
            position: "absolute",
            inset: 0,

            pointerEvents: "none",

            opacity: 0.09,

            backgroundImage:
              "linear-gradient(rgba(255,255,255,0.05) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.05) 1px, transparent 1px)",

            backgroundSize:
              "32px 32px",
          }}
        />

        {/* ======================================================
            LEFT CYAN GLOW
        ====================================================== */}

        <div
          style={{
            position: "absolute",

            width: 280,
            height: 280,

            left: -140,
            top: -190,

            borderRadius: "50%",

            background:
              "rgba(0,246,255,0.08)",

            filter:
              "blur(85px)",

            pointerEvents:
              "none",
          }}
        />

        {/* ======================================================
            RIGHT PURPLE GLOW
        ====================================================== */}

        <div
          style={{
            position: "absolute",

            width: 300,
            height: 300,

            right: -150,
            top: -200,

            borderRadius: "50%",

            background:
              "rgba(139,92,255,0.10)",

            filter:
              "blur(90px)",

            pointerEvents:
              "none",
          }}
        />

        {/* ======================================================
            NAVBAR
        ====================================================== */}

        <nav
          className="navbar navbar-expand-lg"
          style={{
            width: "100%",

            padding:
              "10px 20px",

            position:
              "relative",

            zIndex: 5,
          }}
        >
          <div
            className="container-fluid"
            style={{
              padding:
                "0 10px",

              position:
                "relative",
            }}
          >
            {/* ==================================================
                MOBILE MENU BUTTON
            ================================================== */}

            <button
              type="button"
              aria-label="Toggle navigation"
              aria-expanded={
                mobileMenuOpen
              }
              onClick={() =>
                setMobileMenuOpen(
                  !mobileMenuOpen
                )
              }
              style={{
                width: 44,
                height: 44,

                display: "none",

                alignItems:
                  "center",

                justifyContent:
                  "center",

                border:
                  "1px solid rgba(0,246,255,0.18)",

                borderRadius:
                  "10px",

                background:
                  "rgba(0,246,255,0.035)",

                color:
                  "#ffffff",

                cursor:
                  "pointer",

                fontSize:
                  "22px",

                transition:
                  "all 0.25s ease",
              }}
              className="futuristic-mobile-toggle"
            >
              {mobileMenuOpen
                ? "×"
                : "☰"}
            </button>

            {/* ==================================================
                LOGO
            ================================================== */}

            <Link
              to="/"
              aria-label="Growniverse home"
              style={{
                display:
                  "inline-flex",

                alignItems:
                  "center",

                textDecoration:
                  "none",

                marginRight:
                  "34px",

                position:
                  "relative",

                zIndex: 5,
              }}
            >
              <div
                style={{
                  position:
                    "relative",

                  padding:
                    "5px 7px",

                  border:
                    "1px solid rgba(0,246,255,0.18)",

                  background:
                    "rgba(0,246,255,0.025)",

                  boxShadow:
                    "0 0 22px rgba(0,246,255,0.06)",

                  transition:
                    "all 0.3s ease",
                }}
              >
                <img
                  src={GrowniverseLogo}
                  width="80"
                  height="auto"
                  alt="Growniverse"

                  style={{
                    display:
                      "block",

                    filter:
                      "drop-shadow(0 0 5px rgba(255,255,255,0.80)) drop-shadow(0 0 12px rgba(0,246,255,0.22))",

                    transition:
                      "filter 0.3s ease",
                  }}
                />
              </div>
            </Link>

            {/* ==================================================
                DESKTOP NAVIGATION
            ================================================== */}

            <div
              className="futuristic-desktop-nav"
              style={{
                display:
                  "flex",

                alignItems:
                  "center",

                flex: 1,
              }}
            >
              <ul
                style={{
                  listStyle:
                    "none",

                  padding: 0,
                  margin: 0,

                  display:
                    "flex",

                  alignItems:
                    "center",

                  gap:
                    "8px",
                }}
              >
                {navigationItems.map(
                  (item) => {
                    const active =
                      isActive(
                        item.path
                      );

                    return (
                      <li
                        key={
                          item.path
                        }
                        style={{
                          position:
                            "relative",
                        }}
                      >
                        <Link
                          to={
                            item.path
                          }
                          className={
                            active
                              ? "futuristic-nav-link active"
                              : "futuristic-nav-link"
                          }
                          style={{
                            position:
                              "relative",

                            display:
                              "flex",

                            alignItems:
                              "center",

                            padding:
                              "11px 15px",

                            color:
                              active
                                ? "#ffffff"
                                : "rgba(225,235,245,0.78)",

                            textDecoration:
                              "none",

                            fontSize:
                              "13px",

                            fontWeight:
                              active
                                ? 700
                                : 600,

                            letterSpacing:
                              "0.25px",

                            borderRadius:
                              "8px",

                            transition:
                              "all 0.25s ease",
                          }}
                        >
                          {item.label}

                          {/* Active indicator */}

                          {active && (
                            <span
                              style={{
                                position:
                                  "absolute",

                                left:
                                  "15px",

                                right:
                                  "15px",

                                bottom:
                                  "3px",

                                height:
                                  "2px",

                                borderRadius:
                                  "10px",

                                background:
                                  "linear-gradient(90deg, #00f6ff, #8b5cff)",

                                boxShadow:
                                  "0 0 10px rgba(0,246,255,0.7)",
                              }}
                            />
                          )}
                        </Link>
                      </li>
                    );
                  }
                )}
              </ul>
            </div>

            {/* ==================================================
                LOGIN BUTTON
            ================================================== */}

            <div
              style={{
                marginLeft:
                  "auto",

                display:
                  "flex",

                alignItems:
                  "center",

                position:
                  "relative",

                zIndex: 5,
              }}
            >
              <button
                type="button"
                onClick={
                  openModal
                }

                style={{
                  position:
                    "relative",

                  minWidth:
                    "94px",

                  padding:
                    "10px 21px",

                  border:
                    "1px solid rgba(0,246,255,0.55)",

                  borderRadius:
                    "8px",

                  background:
                    "linear-gradient(135deg, rgba(0,246,255,0.14), rgba(139,92,255,0.12))",

                  color:
                    "#ffffff",

                  fontSize:
                    "13px",

                  fontWeight:
                    700,

                  letterSpacing:
                    "0.5px",

                  cursor:
                    "pointer",

                  boxShadow:
                    "0 0 15px rgba(0,246,255,0.10), inset 0 0 15px rgba(0,246,255,0.03)",

                  transition:
                    "all 0.25s ease",
                }}

                onMouseEnter={(
                  e
                ) => {
                  e.currentTarget.style.background =
                    "linear-gradient(135deg, rgba(0,246,255,0.24), rgba(139,92,255,0.20))";

                  e.currentTarget.style.borderColor =
                    "rgba(0,246,255,0.9)";

                  e.currentTarget.style.boxShadow =
                    "0 0 22px rgba(0,246,255,0.25), inset 0 0 18px rgba(0,246,255,0.05)";

                  e.currentTarget.style.transform =
                    "translateY(-1px)";
                }}

                onMouseLeave={(
                  e
                ) => {
                  e.currentTarget.style.background =
                    "linear-gradient(135deg, rgba(0,246,255,0.14), rgba(139,92,255,0.12))";

                  e.currentTarget.style.borderColor =
                    "rgba(0,246,255,0.55)";

                  e.currentTarget.style.boxShadow =
                    "0 0 15px rgba(0,246,255,0.10), inset 0 0 15px rgba(0,246,255,0.03)";

                  e.currentTarget.style.transform =
                    "translateY(0)";
                }}
              >
                Login
              </button>
            </div>
          </div>
        </nav>

        {/* ======================================================
            MOBILE NAVIGATION
        ====================================================== */}

        {mobileMenuOpen && (
          <div
            style={{
              position:
                "absolute",

              top:
                "100%",

              left: 12,
              right: 12,

              zIndex: 20,

              padding:
                "10px",

              border:
                "1px solid rgba(0,246,255,0.18)",

              borderRadius:
                "12px",

              background:
                "rgba(5,9,18,0.98)",

              backdropFilter:
                "blur(20px)",

              boxShadow:
                "0 20px 50px rgba(0,0,0,0.65), 0 0 25px rgba(0,246,255,0.06)",
            }}
            className="futuristic-mobile-menu"
          >
            {navigationItems.map(
              (item) => {
                const active =
                  isActive(
                    item.path
                  );

                return (
                  <Link
                    key={
                      item.path
                    }
                    to={
                      item.path
                    }

                    style={{
                      display:
                        "block",

                      padding:
                        "13px 15px",

                      margin:
                        "2px 0",

                      borderRadius:
                        "8px",

                      textDecoration:
                        "none",

                      color:
                        active
                          ? "#ffffff"
                          : "#cbd5e1",

                      fontSize:
                        "14px",

                      fontWeight:
                        active
                          ? 700
                          : 600,

                      background:
                        active
                          ? "linear-gradient(90deg, rgba(0,246,255,0.10), rgba(139,92,255,0.07))"
                          : "transparent",

                      borderLeft:
                        active
                          ? "2px solid #00f6ff"
                          : "2px solid transparent",

                      transition:
                        "all 0.2s ease",
                    }}
                  >
                    {item.label}
                  </Link>
                );
              }
            )}
          </div>
        )}

        {/* ======================================================
            BOTTOM SCAN LINE
        ====================================================== */}

        <div
          style={{
            position:
              "absolute",

            bottom: 0,

            left: "15%",
            right: "15%",

            height: 1,

            background:
              "linear-gradient(90deg, transparent, rgba(0,246,255,0.45), transparent)",

            pointerEvents:
              "none",

            zIndex: 3,
          }}
        />
      </header>

      {/* ========================================================
          LOGIN / SIGNUP POPUP
      ======================================================== */}

      {isModalOpen && (
        <LoginAndSignupPopup
          onClose={
            closeModal
          }
        />
      )}

      {/* ========================================================
          RESPONSIVE CSS
      ======================================================== */}

      <style>
        {`
          .futuristic-nav-link:hover {
            color: #ffffff !important;
            background: rgba(0,246,255,0.055);
            transform: translateY(-1px);
          }

          .futuristic-nav-link.active {
            color: #ffffff !important;
          }

          .futuristic-mobile-menu a:hover {
            color: #00f6ff !important;
            background: rgba(0,246,255,0.07) !important;
          }

          @media (max-width: 991px) {
            .futuristic-desktop-nav {
              display: none !important;
            }

            .futuristic-mobile-toggle {
              display: flex !important;
              margin-right: 12px;
            }

            .futuristic-mobile-menu {
              display: block;
            }
          }

          @media (min-width: 992px) {
            .futuristic-mobile-menu {
              display: none;
            }
          }

          @media (max-width: 576px) {
            .futuristic-mobile-toggle {
              width: 40px !important;
              height: 40px !important;
              font-size: 20px !important;
            }

            .futuristic-mobile-menu {
              left: 8px !important;
              right: 8px !important;
            }
          }
        `}
      </style>
    </>
  );
};

export default Header;